import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPublicProducts, type PublicProduct } from '@/components/subscriptions/lib/public-products-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import logger from '@/lib/logging';
import { createCheckoutSessionApi } from '@/lib/billing-api';
import { BROWSE_PLANS_PATH } from '@/components/subscriptions/lib/plans-path';

function formatPrice(amount: number | null, currency: string | null, interval: string | null): string {
  if (amount == null) return 'Contact us';
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(amount / 100);
  if (!interval || interval === 'one_time') return formatted;
  return `${formatted} / ${interval}`;
}

function findOffering(products: PublicProduct[], priceId: string) {
  for (const product of products) {
    const price = product.prices.find((p) => p.priceId === priceId);
    if (price) {
      return { product, price };
    }
  }
  return null;
}

export function PurchasePage() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const priceId = searchParams.get('priceId')?.trim() ?? '';
  const { toast } = useToast();
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchasePath = useMemo(() => {
    const query = priceId ? `?priceId=${encodeURIComponent(priceId)}` : '';
    return `/purchase${query}`;
  }, [priceId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setCatalogLoading(true);
      try {
        const catalog = await fetchPublicProducts();
        if (!cancelled) setProducts(catalog);
      } catch (err) {
        logger.error('[PurchasePage] catalog load failed', err);
        if (!cancelled) setError('Unable to load product details.');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!priceId) {
    return <Navigate to={BROWSE_PLANS_PATH} replace />;
  }

  if (authLoading || catalogLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const loginHref = `/login?returnTo=${encodeURIComponent(purchasePath)}`;
    return <Navigate to={loginHref} replace />;
  }

  const offering = findOffering(products, priceId);
  if (!offering) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Product unavailable</CardTitle>
            <CardDescription>
              This subscription offering is not available. Choose another plan on the welcome page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to={BROWSE_PLANS_PATH}>Back to plans</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleContinue = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await createCheckoutSessionApi(priceId);
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError('Checkout did not return a payment URL.');
    } catch (err) {
      logger.error('[PurchasePage] checkout-session failed', err);
      const message =
        err instanceof Error ? err.message : 'Could not start checkout. Please try again.';
      setError(message);
      toast({ variant: 'destructive', title: 'Checkout unavailable', description: message });
    } finally {
      setSubmitting(false);
    }
  };

  const { product, price } = offering;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <CardTitle>Confirm your subscription</CardTitle>
          <CardDescription>Review your selection before continuing to secure payment.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-border p-4 space-y-1">
            <p className="font-semibold text-lg">{product.name}</p>
            {product.description ? (
              <p className="text-sm text-muted-foreground">{product.description}</p>
            ) : null}
            <p className="text-xl font-medium pt-2">
              {formatPrice(price.amount, price.currency, price.interval)}
            </p>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Unable to continue</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button className="flex-1" size="lg" onClick={handleContinue} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting…
                </>
              ) : (
                'Continue to payment'
              )}
            </Button>
            <Button variant="outline" className="flex-1" asChild>
              <Link to={BROWSE_PLANS_PATH}>Choose another plan</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PurchasePage;
